import React, { useState } from "react";
import Button from "react-bootstrap/Button";
import Collapse from "react-bootstrap/Collapse";
import { FormattedMessage } from "react-intl";
import useAuthorizedAction, {
  AuthorizedActionsKeys,
} from "../../../hooks/useAuthorizedAction";
import { PropsWithClassName } from "../../../models/props-with-class-name";
import ApprovedIcon from "../../Shared/ApprovedIcon/ApprovedIcon";
import CircleIndicator from "../../Shared/CircleIndicator/CircleIndicator";
import InterpretationIcon from "../../Shared/InterpretationIcon/InterpretationIcon";
import ReviewPriorityIndicator from "../../Shared/ReviewTimeIndicator/ReviewPriorityIndicator";
import UrgentIcon from "../../Shared/UrgentIcon/UrgentIcon";
import styles from "./Legend.module.css";

type LegendProps = {
  collapsed: boolean;
  onCollapse?: () => void;
};

const Legend = ({
  collapsed,
  onCollapse,
  className,
}: PropsWithClassName<LegendProps>) => {
  const [isCollapsed, setIsCollapse] = useState(collapsed);

  const onCollapseToggleHandler = () => {
    setIsCollapse((prev) => !prev);
    if (onCollapse) onCollapse();
  };

  const isReviewer = useAuthorizedAction(AuthorizedActionsKeys.ReviewEcg);
  const isCroOrg = useAuthorizedAction(AuthorizedActionsKeys.ViewAsCroOrg);
  const isCroExternOrg = useAuthorizedAction(
    AuthorizedActionsKeys.ViewAsCroExternOrg
  );
  const isApprover = useAuthorizedAction(AuthorizedActionsKeys.ApproveEcg);

  return (
    <div
      className={`${styles.legend} ${className}  text-nowrap text-sm-none container p-2`}
    >
      <div className="d-flex align-items-center">
        <div className="flex-grow-1">
          <span className="font-weight-bold">
            <FormattedMessage id="TREE.LEGEND.TITLE" defaultMessage="Legend" />
          </span>
        </div>
        <div>
          <Button
            variant="link"
            className={`${styles.showToggle} p-0`}
            onClick={onCollapseToggleHandler}
          >
            <small>
              <FormattedMessage
                id={
                  isCollapsed
                    ? "TREE.LEGEND.ACTION.SHOW"
                    : "TREE.LEGEND.ACTION.HIDE"
                }
                defaultMessage={isCollapsed ? "SHOW" : "HIDE"}
              />
            </small>
          </Button>
        </div>
      </div>
      <Collapse in={!isCollapsed} timeout={200}>
        <div>
          <div className="d-flex align-items-center mt-1">
            <div className="d-sm-flex flex-row align-items-center">
              {/* Reviewer - Time to review */}
              {isReviewer && (
                <div className="d-flex flex-row align-items-center">
                  <span>
                    <FormattedMessage
                      id="LEGEND.SLA_TIME"
                      defaultMessage="SLA time (min)"
                    />
                    :
                  </span>
                  <span className="mx-1"></span>
                  <ReviewPriorityIndicator priority={"high"} showTimeSymbol />
                  <span className="mx-1"></span>
                  <ReviewPriorityIndicator priority={"low"} showTimeSymbol />
                  <span className="mx-1"></span>
                  <ReviewPriorityIndicator priority={"none"} showTimeSymbol />
                  <span
                    className={`${styles.divider} d-none d-sm-inline-block`}
                  ></span>
                </div>
              )}

              {/* High Priority */}
              <div className="d-flex flex-row align-items-center mx-n2 mx-sm-0 mt-1 mt-sm-0 ">
                <UrgentIcon className={styles.urgentIcon} />
                <FormattedMessage
                  id="ECG_DETAILS.HIGH_PRIORITY"
                  defaultMessage="High Priority"
                />
                <>
                  {(isCroOrg || isCroExternOrg) && isApprover ? (
                    <>
                      {/* Approved */}
                      <span className="mx-2"></span>
                      <div className="d-flex flex-row align-items-center">
                        <ApprovedIcon />
                        <span className="mx-1"></span>
                        <FormattedMessage
                          id="ECG_DETAILS.REVIEWED"
                          defaultMessage="Reviewed"
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Findings */}
                      <span className="mx-2"></span>
                      <div className="d-flex flex-row align-items-center">
                        <CircleIndicator className={styles.findings} />
                        <span className="mx-sm"></span>
                        <FormattedMessage
                          id="ECG_DETAILS.FINDINGS"
                          defaultMessage="Findings"
                        />
                      </div>
                    </>
                  )}

                  <span className="mx-2"></span>

                  {/* Interpretations */}
                  <div className="d-flex flex-row align-items-center">
                    <InterpretationIcon selected />
                    <span className="mx-1"></span>
                    <FormattedMessage
                      id="ECG_DETAILS.INTERPRETATION"
                      defaultMessage="Interpretation"
                    />
                  </div>
                </>
              </div>
            </div>
          </div>
        </div>
      </Collapse>
    </div>
  );
};

export default Legend;
